export const PHOSPHATE_ARSENATE_VANADATE_LOGIC = {
  description: "Phosphates, Arsenates & Vanadates Identification Rules",
  
  coreRules: `
=== 磷、砷、钒的氧化带与伟晶岩鉴定法则 (Phosphates, Arsenates & Vanadates Protocol) ===

[8.1 伟晶岩与火成岩中的基础磷酸盐 (The Pegmatite Phosphates)]
*   **磷灰石 (Apatite) 与 氯磷灰石**: 磷酸盐矿物的绝对基准。寻找**六方柱状晶体 (Hexagonal prisms)**，通常端面平直，呈现迷人的黄绿色、海蓝色或灰色。光泽通常为明显的玻璃光泽到亚树脂光泽。
*   **独居石 (Monazite) 与 磷钇矿 (Xenotime)**: 稀土的宝库，具有强放射性。
    *   **独居石**: 寻找**红褐色、黄褐色至棕色，且带有强烈的树脂光泽 (Resinous luster) 甚至金刚光泽**的楔形/厚板状晶体。它的颜色通常像凝固的深色树脂或太妃糖。
    *   **磷钇矿**: 与锆石极像，通常呈现暗褐色的四方双锥晶体，但硬度比锆石软。
*   **天蓝石 (Lazulite)**: 颜色锚点！呈现**极其深邃、纯粹的天蓝色到深蓝色 (Deep azure to indigo blue)**。它通常是致密块状或尖锐的双锥晶体，深深嵌在石英等基质岩石中，不会像胆矾那么晶莹剔透，显得更加致密且坚硬。
*   **紫磷锰矿 (Purpurite / Heterosite)**: 终极颜色锚点！不可能错认的**极其显眼的紫红色、洋紫红 (Deep magenta / Vivid purple)**。它通常作为伟晶岩伴生的次生块状氧化块出现，呈现土状或致密的极紫色块体。
*   **蓝铁矿 (Vivianite)**: “属于墓地的矿物”。寻找**深蓝绿色、蓝黑色、靛蓝色的长透明刀片状/板状晶簇 (Dark blue/green bladed crystals)**。它在新开采时经常是无色/浅绿色的，但只要接触到阳光就会迅速变蓝、变黑。
*   **暧昧石 (Amblygonite / 磷铝锂石)**: 含锂的重要伟晶岩矿物。常呈现白色、灰白色或灰蓝色的大块解理面，长得像长石，但比长石致密且在紫外灯下可能没有特征反应，燃烧带微弱红焰（锂）。

[8.2 极具辨识度的次生结构磷酸盐 (Radiating & Botryoidal Phosphates)]
*   **银星石 (Wavellite)**: 拥有全矿物界最惊人的内部结构之一！切开或打碎后，截面必定呈现**极其完美的“放射状星芒”或“车轮/风车”构造 (Radiating starburst or pinwheel structures)**，多为浅绿色、淡黄色或白色交替的同心圆团块。
*   **绿松石 (Turquoise)**: 经典宝石。**不透明的天蓝色至苹果绿 (Opaque blue to green)**，经常伴有黑色的铁线或褐色的基质网纹，无解理，呈现典型的蜡状至土状光泽。
*   **磷氯铅矿 (Pyromorphite)**: 极度炫目的荧光级视觉。寻找**翠绿色、黄绿色的，具有明显的“六方桶状 (Barrel-shaped)”、甚至是中空的管状晶簇**。常常密集堆积在岩石上，看起来像长满绿色小蘑菇或苔藓般的六方柱。

[8.3 恐怖的砷酸盐“死亡之花” (The Toxic Arsenate Blooms)]
这些常常是富含重金属和剧毒的干旱氧化带次生矿。
*   **钴华 (Erythrite)**: 钴矿的找矿标志，绝对颜色锚点。呈现**极其妖艳的洋红色、紫红色 (Crimson/Magenta)**。如果看到石头上长着一簇簇紫红色的纤细毛发状结晶，或是一层粉红色的霜状粉末（Cobalt bloom），那就是钴华。
*   **磷砷铅矿 (Mimetite)**: 与磷氯铅矿是亲戚且外观类似，但颜色更是**黄橙色、橙褐色**，并且更容易长成**葡萄状、球状簇 (Globular / Botryoidal crusts)** 而不仅仅是直挺挺的六方柱。
*   **砷钙铜矿 (Conichalcite)** 与 **砷铜铅矿 (Bayldonite 等)**: 典型标志是在含铜裂隙中呈现**极度致密的苹果绿、翠绿色葡萄状/肾状皮壳 (Bright green botryoidal crusts)**。
*   **砷铋铜矿 (Mixite)**: 极细的蓝绿色、翠绿色**毛发状、放射毛丝状晶簇**。

[8.4 钒酸盐系列与致命的放射性结皮 (Vanadates & Radioactive Crusts)]
*   **钒铅矿家族 (Vanadinite / Descloizite / Mottramite)**:
    *   **钒铅矿 (Vanadinite)**: 本应是鲜艳夺目的红/橙红色完美六方短柱。但在某些标本中（如你的图），如果矿物呈现褐色、暗红色晶簇皮壳，也有可能。
    *   **钒铅锌矿 (Descloizite)** & **钒铜铅矿 (Mottramite)**: 通常呈现**深褐黑色、暗绿黑色的极度复杂的密集“锥状”、“葡萄状”细小结晶皮壳 (Dark brownish-black drusy or botryoidal crusts)**。它看起来就像给石头覆盖了一层带有金属光泽的巧克力色黑色糖霜或黑色小棘刺。
*   **铀的诅咒体系 (The Uranium Micas)**:
    *   **变铜铀云母 (Metatorbernite/Torbernite)**: 不要被它绝美的绿色欺骗。寻找**具有极其平整的正方形或长方形轮廓的深翠绿色薄片 (Emerald green square tabular crystals)**。它像绿色的方形云母片一样长在石头上。带有强烈的放射性！
    *   **钙钒铀矿 (Tyuyamunite) / 矾钾铀矿 (Carnotite)**: 终极黄色警告。寻找在砂岩或裂隙表面涂抹的**极其鲜艳明亮的“金丝雀黄”(Canary-yellow)、亮黄色粉末状、土状结皮**。强放射性！

`,

  diagnosticTests: `
- 打碎或观察断面，如果看到了几乎完美的圆形放射状“星芒（风车）”，它是银星石。
- 如果矿物呈现毫无生机的剧烈洋红色/紫红色致密块体，是紫磷锰铁矿；如果是呈现毛发状、针状晶簇的紫红色，那是钴华 (砷)。
- 评估绿色矿物的形态：如果是六方柱带点桶状/空心状结构，是磷氯铅矿；如果是正方形小薄片，极其危险，是各种铀云母（如变铜铀云母）。
- 发现极其鲜艳的明黄色土状/粉末状结皮，高度预警钒铀矿体系。
- 对于深蓝色的块体：如果是透明/半透明的刀片状晶簇变黑变蓝的，是蓝铁矿；如果是深嵌入岩石的致密天蓝色晶体，是天蓝石。
- 如果岩石表面覆盖了一层黑色、暗褐色的极其细小但密集的锥状结晶皮壳，重点思考钒铅矿族（钒铅锌矿 / 钒铜铅矿）。
- 对于带强树脂光泽的黄褐色/红褐色厚板晶体，不仅要想到闪锌矿，在伟晶岩/变质岩必须考虑强放射性的独居石。
`
};
